import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { Close, Search } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  categorizeNotesTime,
  notesManipulator,
} from "../../../../../../helpers/manuplator";
import NoteDetail from "./note-detail";
import NotesListing from "./notes-listing";
import { generateUUID } from "../../../../../../utils";
import { BeatLoader } from "react-spinners";
import "./styles.scss";

const NotesModal = ({ preview, previewHandler }) => {
  // STATES
  const [searchText, setsearchText] = useState("");
  const [notesLoader, setNotesLoader] = useState(false);
  const [saveLoader, setsaveLoader] = useState([]);
  const [deletePreview, setDeletePreview] = useState(false);
  const [deleteLoader, setdeleteLoader] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);

  // REDUX DATA
  const user = useSelector((state) => state.UserReducer.profile);
  const selectedCompany = useSelector(
    (state) => state.CompanyReducer.selected_company
  );

  // CONST VALS
  const { http } = global.services;
  const filteredNotes =
    searchText.length === 0
      ? notes
      : notes.filter((note) =>
          note.note_title.toLowerCase().includes(searchText.toLowerCase())
        );

  // HELPERS
  const createNoteHelper = (data) => {
    const payload = {
      note_title: data.note_title,
      notes: data.notes,
    };
    setsaveLoader([...saveLoader, data.id]);
    http
      .post(`company-notes/${selectedCompany.id}`, payload)
      .finally(() => setsaveLoader([]))
      .then((res) => {
        setNotes((notes) => {
          const lastNewNoteIndex = notes
            .slice()
            .reverse()
            .findIndex((note) => note.isNew);
          const adjustedIndex =
            lastNewNoteIndex !== -1 ? notes.length - 1 - lastNewNoteIndex : -1;
          const updatedNotes = notes.map((note, index) => {
            if (index === adjustedIndex) return notesManipulator([res.data])[0];
            return note;
          });
          return updatedNotes;
        });

        if (
          selectedNote?.isNew &&
          selectedNote?.note_title === data.note_title
        ) {
          setSelectedNote((selectedNote) => {
            if (
              selectedNote?.isNew &&
              selectedNote?.note_title === data.note_title
            ) {
              return notesManipulator([res.data])[0];
            }
            return selectedNote;
          });
        }
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  };

  const updateNoteHelper = (data) => {
    const payload = {
      id: data.id,
      note_title: data.note_title,
      pinned: data.pinned,
      notes: data.notes,
    };
    setsaveLoader([...saveLoader, data.id]);
    http
      .put(`company-notes/${selectedCompany.id}`, payload)
      .finally(() => setsaveLoader([]))
      .then((res) => {
        setNotes((notes) => {
          const updatedNotes = notes.map((note) => {
            if (note.id === res.data.id) {
              note = notesManipulator([res.data])[0];
            }
            return note;
          });
          return updatedNotes;
        });

        setSelectedNote((selectedNote) => {
          if (selectedNote.id === res.data.id) {
            return notesManipulator([res.data])[0];
          }
          return selectedNote;
        });
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  };

  const sortNotesHelper = (notes) => {
    setNotes(notes);
    http
      .put(`company-notes/${selectedCompany.id}/sort`, {
        notes: notes.map((note, index) => ({
          id: note.id,
          name: note.note_title,
          pinned: note.pinned,
          order: index,
        })),
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  };

  // HANDLERS
  const deleteNoteHandler = () => {
    if (selectedNote.isNew) {
      const updatedNotes = notes.filter((note) => note.id !== selectedNote.id);
      setNotes(updatedNotes);
      setSelectedNote(updatedNotes[0]);
      setDeletePreview(false);
      return;
    }
    setdeleteLoader(true);
    http
      .delete(`company-notes/${selectedNote.id}`)
      .finally(() => setdeleteLoader(false))
      .then(() => {
        const updatedNotes = notes.filter(
          (note) => note.id !== selectedNote.id
        );
        setNotes(updatedNotes);
        setSelectedNote(updatedNotes[0]);
        setDeletePreview(false);
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  };

  const saveHandler = (value) => {
    const data = selectedNote;
    const payload = { ...data, notes: value.toString("html") };
    if (data.isNew) {
      if (data.note_title !== "") createNoteHelper(payload);
    } else {
      if (data.notes === value.toString("html")) return;
      updateNoteHelper(payload);
    }
  };

  const createNoteHandler = () => {
    const data = {
      id: generateUUID(),
      company_id: selectedCompany.id,
      note_title: "",
      notes: "",
      created_by: (user.first_name ?? "") + " " + (user.last_name ?? ""),
      created_at: categorizeNotesTime(new Date()),
      isNew: true,
    };
    setNotes([data, ...notes]);
    setSelectedNote(data);
  };

  const updateNameHandler = (id, value) => {
    const updatedNotes = [...notes].map((note) => {
      if (note.id === id) {
        note.note_title = value;
      }
      return note;
    });
    setNotes(updatedNotes);
    setSelectedNote({
      ...selectedNote,
      note_title: value,
    });
  };

  const selectNoteHandler = (data) => {
    setSelectedNote(data);
  };

  const pinUnPinNoteHandler = (data) => {
    setNotes((notes) => {
      const updatedNotes = notes.map((note) => {
        if (note.id === data.id) {
          note.pinned = data.pinned;
        }
        return note;
      });
      return updatedNotes;
    });
    updateNoteHelper(data);
  };

  // HOOKS
  useEffect(() => {
    if (!preview) return;
    setNotesLoader(true);
    http
      .get(`company-notes/${selectedCompany.id}`)
      .finally(() => setNotesLoader(false))
      .then(({ data }) => {
        setNotes(notesManipulator(data));
        if (data.length > 0) setSelectedNote(notesManipulator([data[0]])[0]);
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  }, [preview]);

  return (
    <>
      <Modal
        visible={preview}
        onCancel={previewHandler}
        closable={false}
        footer={null}
        width={1300}
        className="notes-modal"
      >
        <div className="top-box">
          <button className="close-btn" onClick={previewHandler}>
            <Close />
          </button>
          <h2>Notes</h2>
          <p>
            Welcome to your company's notes repository. This is your space to
            create, share, and organize meeting notes, ideas, and important
            information. Keep everyone aligned and up to date by collaborating
            on key discussions and insights.
          </p>
        </div>
        <div className="filter-box">
          <div className="search-box">
            <Search />
            <input
              type="text"
              placeholder="Search notes"
              value={searchText}
              onChange={({ target }) => setsearchText(target.value)}
            />
          </div>
          <button className="create-btn" onClick={createNoteHandler}>
            Create
          </button>
        </div>
        <div className="notes-wrapper">
          <NotesListing
            isLoading={notesLoader}
            data={filteredNotes}
            selectedNote={selectedNote}
            sortNotes={sortNotesHelper}
            selectNote={selectNoteHandler}
            saveLoader={saveLoader}
            pinToggleHandler={pinUnPinNoteHandler}
          />
          {selectedNote && (
            <NoteDetail
              data={selectedNote}
              updateName={updateNameHandler}
              saveHandler={saveHandler}
              deleteToggle={() => setDeletePreview(true)}
            />
          )}
        </div>
      </Modal>
      <Modal
        visible={deletePreview}
        centered
        className="delete-doc-modal"
        footer={null}
        onCancel={() => setDeletePreview(false)}
        closable={false}
      >
        <button className="close-btn" onClick={() => setDeletePreview(false)}>
          <Close />
        </button>
        <h2>Delete Note</h2>
        <p>
          Are you sure you want to delete{" "}
          {selectedNote?.note_title === ""
            ? "New Note"
            : selectedNote?.note_title}
          ?
        </p>
        <div className="action-box">
          <button onClick={() => setDeletePreview(false)}>Cancel</button>
          <button className="delete" onClick={deleteNoteHandler}>
            {deleteLoader ? <BeatLoader size={8} color="#fff" /> : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default NotesModal;
